import { DateTime } from "luxon";
import {
  MySubscriptionDto,
  SubscriptionStatus,
} from "common/models/types";

export type SubscriptionUiAction =
  | "subscribe"
  | "manage"
  | "changeCard"
  | "resume"
  | "refresh"
  | null;

export type SubscriptionBodyKind =
  | "loading"
  | "ad"
  | "pending"
  | "active"
  | "cancelling"
  | "cancelled"
  | "past_due"
  | "expired";

export interface SubscriptionUiState {
  bodyKind: SubscriptionBodyKind;
  primaryAction: SubscriptionUiAction;
  badge: "neutral" | "info" | "warning" | "danger";
}

export const getSubscriptionUiState = (
  me: MySubscriptionDto | null,
  hasLoadedOnce: boolean
): SubscriptionUiState => {
  // 첫 진입 깜빡임 방지
  if (!hasLoadedOnce && me === null) {
    return { bodyKind: "loading", primaryAction: null, badge: "neutral" };
  }
  const status: SubscriptionStatus = me?.status || "NONE";
  switch (status) {
    case "NONE":
      return { bodyKind: "ad", primaryAction: "subscribe", badge: "neutral" };
    case "PENDING":
      return { bodyKind: "pending", primaryAction: "refresh", badge: "info" };
    case "ACTIVE":
      // primary 액션 없음 — 카드 변경/해지 보조 버튼은 후속 PR에서 추가.
      // 이전엔 "manage"가 Pricing 페이지 이동을 의미했으나 Pricing이 안내 전용으로
      // 단순화되면서 의미가 모호해져 일시적으로 비노출.
      return { bodyKind: "active", primaryAction: null, badge: "info" };
    case "CANCELLING":
      return { bodyKind: "cancelling", primaryAction: null, badge: "warning" };
    case "CANCELLED":
      return { bodyKind: "cancelled", primaryAction: "resume", badge: "warning" };
    case "PAST_DUE":
      return { bodyKind: "past_due", primaryAction: "changeCard", badge: "danger" };
    case "EXPIRED":
      return { bodyKind: "expired", primaryAction: "subscribe", badge: "neutral" };
    default:
      return { bodyKind: "ad", primaryAction: "subscribe", badge: "neutral" };
  }
};

/**
 * 백엔드 날짜 문자열을 KST 기준으로 포맷. 파싱 실패/null이면 "-" 반환.
 *
 * 두 직렬화 형식을 모두 처리한다(백엔드 엔드포인트별 불일치):
 * - ISO8601(+Z) — 예: `/subscriptions/me`, `/admin/:id/payments` → UTC로 해석 후 KST 변환
 * - "YYYY-MM-DD HH:mm:ss" naive — 예: `/admin`(목록) → 백엔드가 KST 벽시계로 직렬화하므로 KST로 해석
 *   (근거: 동일 구독의 periodStart가 목록 "2026-06-24 17:33:21" = 결제이력 "2026-06-24T08:33:21Z")
 */
export const formatKstDate = (value: string | null, withTime = false): string => {
  if (!value) return "-";
  const isoUtc = DateTime.fromISO(value, { zone: "utc" });
  const dt = isoUtc.isValid
    ? isoUtc.setZone("Asia/Seoul")
    : DateTime.fromSQL(value, { zone: "Asia/Seoul" });
  if (!dt.isValid) return "-";
  return dt.toFormat(withTime ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd");
};

/** 정수 원화 포맷 */
export const formatKrw = (amount: number): string =>
  `${amount.toLocaleString("ko-KR")}원`;
